import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'Flipkart Seller Management Services | Mad Marketing Club',
  description: 'Professional Flipkart seller management services in India — account setup, catalog optimization, Flipkart Ads, inventory management & revenue growth. Get a free audit.',
  canonical: 'https://madmarketingclub.com/services/flipkart',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Flipkart', url: 'https://madmarketingclub.com/services/flipkart' },
  ],
  service: { name: 'Flipkart Seller Management Services', type: 'Flipkart Marketplace Management' },
};

const OVERVIEW_POINTS = [
  'Access millions of active buyers across India',
  'Reach customers across 19,000+ serviceable pincodes',
  'Participate in major sale events like Big Billion Days',
  'Leverage Flipkart Advertising for visibility',
  'Utilize fulfillment and logistics support',
  'Access seller insights and analytics',
  'Expand into multiple product categories',
  'Scale through marketplace growth programs',
];

const APPROACH = [
  'Seller account operations',
  'Product catalog optimization',
  'Flipkart SEO',
  'Flipkart Ads management',
  'Inventory forecasting',
  'Pricing optimization',
  'Fulfillment support',
  'Performance monitoring',
  'Marketplace expansion',
];

const PROCESS = [
  {
    num: '01',
    title: 'Business Consultation & Discovery',
    intro: 'We begin by understanding:',
    points: [
      'Your products',
      'Brand positioning',
      'Existing marketplace presence',
      'Competitor landscape',
      'Revenue goals',
      'Growth opportunities',
    ],
  },
  {
    num: '02',
    title: 'Flipkart Account Audit',
    intro: 'We analyze:',
    points: [
      'Product listings',
      'Search visibility',
      'Catalog quality',
      'Ad performance',
      'Pricing competitiveness',
      'Inventory health',
      'Account performance metrics',
    ],
  },
  {
    num: '03',
    title: 'Marketplace Growth Strategy',
    intro: 'A customized growth roadmap includes:',
    points: [
      'SEO strategy',
      'Catalog optimization plan',
      'Advertising roadmap',
      'Inventory planning',
      'Promotional strategy',
      'Revenue growth targets',
    ],
  },
  {
    num: '04',
    title: 'Execution & Optimization',
    intro: 'We implement:',
    points: [
      'Product listings',
      'SEO improvements',
      'Advertising campaigns',
      'Brand content',
      'Pricing optimization',
      'Catalog enhancements',
      'Operational improvements',
    ],
  },
  {
    num: '05',
    title: 'Reporting & Continuous Growth',
    intro: 'Regular reporting covers:',
    points: [
      'Sales performance',
      'Advertising ROI',
      'Keyword visibility',
      'Conversion rates',
      'Inventory performance',
      'Growth opportunities',
    ],
  },
];

const SERVICES = [
  {
    title: 'Flipkart Seller Account Setup & Onboarding',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'Seller Hub registration assistance',
      'GST & documentation guidance',
      'Account verification support',
      'Store setup',
      'Category onboarding',
      'Business profile setup',
      'Initial marketplace configuration',
    ],
  },
  {
    title: 'Product Listing & Catalog Management',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'Product listing creation',
      'Bulk catalog uploads',
      'Product attribute optimization',
      'Variation creation',
      'Listing error resolution',
      'Catalog corrections',
      'Premium catalog optimization',
    ],
  },
  {
    title: 'Flipkart SEO Services',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'Competitor analysis',
      'Product title optimization',
      'Description enhancement',
      'Attribute optimization',
      'Search visibility improvement',
      'Conversion-focused content',
    ],
  },
  {
    title: 'Flipkart Advertising Management',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Sponsored product campaigns',
      'Search visibility campaigns',
      'Bid optimization',
      'Budget management',
      'Keyword optimization',
      'ROAS improvement',
      'Campaign scaling',
    ],
  },
  {
    title: 'Brand Store & Creative Optimization',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    points: [
      'Brand store setup',
      'Product image optimization',
      'Infographic design',
      'Enhanced content creation',
      'Product photography consultation',
      'Conversion-focused creatives',
    ],
  },
  {
    title: 'Fulfillment & Logistics Support',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    points: [
      'Fulfillment strategy consulting',
      'Fulfillment by Flipkart guidance',
      'Shipping workflow optimization',
      'Inventory allocation',
      'Order management',
      'Return process support',
    ],
  },
  {
    title: 'Inventory & Pricing Management',
    gradient: 'linear-gradient(135deg, #0369a1, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    points: [
      'Inventory forecasting',
      'Stock planning',
      'Inventory health monitoring',
      'Pricing optimization',
      'Competitive price analysis',
      'Seasonal inventory planning',
    ],
  },
  {
    title: 'Account Health & Performance Management',
    gradient: 'linear-gradient(135deg, #b45309, #d97706)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    points: [
      'Account monitoring',
      'Performance score tracking',
      'Policy compliance management',
      'Seller support coordination',
      'Issue resolution',
      'Operational optimization',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Sales reporting',
      'Advertising reporting',
      'Product performance analysis',
      'Profitability tracking',
      'Inventory reporting',
      'Competitor benchmarking',
      'Marketplace dashboards',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated marketplace specialists',
  'SEO + Advertising integration',
  'Performance-focused optimization',
  'Transparent reporting',
  'Scalable growth strategies',
  'Marketplace-specific expertise',
  'End-to-end account management',
];

const INDUSTRIES = [
  'Fashion & Apparel',
  'Beauty & Personal Care',
  'Electronics',
  'Home & Kitchen',
  'Consumer Goods',
  'FMCG',
  'Health & Wellness',
  'Pet Products',
  'D2C Brands',
  'Lifestyle Brands',
];

const STAGES = [
  'Multi-category expansion',
  'Product portfolio growth',
  'New market opportunities',
  'Brand scaling strategies',
  'Big Billion Days planning',
  'Seasonal campaigns',
  'Festival sale preparation',
  'Product launch campaigns',
];

const ADDITIONAL = [
  'Discount strategy management',
  'Promotional campaign management',
  'Seller score improvement',
  'Profitability analysis',
  'Competitor benchmarking',
  'Category expansion support',
  'New product launch strategy',
  'Review & rating monitoring',
];

const FAQS = [
  {
    q: 'Do you help with Flipkart seller registration?',
    a: 'Yes. We assist with onboarding, account setup, documentation guidance, and seller account activation.',
  },
  {
    q: 'Can you manage Flipkart advertising campaigns?',
    a: 'Yes. We create, optimize, and scale Flipkart Ads campaigns focused on visibility, traffic, and sales growth.',
  },
  {
    q: 'Do you help with catalog optimization?',
    a: 'Yes. We optimize product listings, titles, descriptions, attributes, images, and catalog structure for better rankings and conversions.',
  },
  {
    q: 'Can you support fulfillment and logistics?',
    a: 'Yes. We help optimize fulfillment workflows and guide eligible sellers on Fulfillment by Flipkart (FBF).',
  },
  {
    q: 'Do you provide monthly reporting?',
    a: 'Yes. We provide performance reports covering sales, advertising, inventory, profitability, and growth opportunities.',
  },
];

export default function FlipkartPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Flipkart Marketplace</span>
          </div>
        </nav>

        {/* HERO — single column, no image */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">Flipkart Marketplace</div>
            <h1 className="sp-hero-h1">Flipkart Marketplace Management Services</h1>
            <p className="sp-hero-sub">Complete Flipkart Seller Account Management & Growth Solutions</p>
            <p className="sp-hero-desc">Grow your business on Flipkart with end-to-end marketplace management services designed to increase visibility, improve conversions, optimize advertising performance, and scale revenue. We help manage every aspect of your Flipkart operations — from onboarding and catalog management to Flipkart Ads, fulfillment, inventory planning, and performance optimization.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">45Cr+</span><span className="sp-hero-stat-lbl">Customers Reached</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">19,000+</span><span className="sp-hero-stat-lbl">Pincodes Serviced</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Full</span><span className="sp-hero-stat-lbl">Account Management</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW — with Image 1 */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">Sell & Scale on Flipkart with a Performance-Driven Strategy</h2>
                <p className="sp-para">Flipkart is one of India's largest e-commerce marketplaces, enabling businesses to sell products across thousands of cities and pin codes nationwide. Through Flipkart Seller Hub, sellers gain access to customer reach, logistics support, advertising solutions, analytics tools, and growth programs.</p>
                <p className="sp-para">Selling on Flipkart gives your brand:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="Flipkart marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">End-to-End Flipkart Marketplace Management</h2>
            <p className="sp-para">Selling successfully on Flipkart requires more than listing products. Marketplace growth depends on catalog optimization, SEO, advertising, pricing strategy, inventory management, fulfillment efficiency, and account performance monitoring.</p>
            <h3 className="sp-h3">Our team works as your dedicated Flipkart growth partner by managing:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
            <p className="sp-para">We focus on building sustainable growth through performance optimization, data-driven strategies, and conversion-focused execution across India's largest marketplace.</p>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your Flipkart Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE — with Image 2 */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Flipkart Marketplace Services</div>
            <h2 className="sp-h2">Growth-Focused Marketplace Experts</h2>
            <p className="sp-para">We combine marketplace strategy, SEO, advertising management, catalog optimization, and operational expertise to help brands scale on Flipkart.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">Benefits of Working With Us</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="Flipkart marketplace management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Serve</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Marketplace Growth Solutions for Every Stage</h2>
            <p className="sp-para">Whether you're looking to grow through:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured marketplace growth strategy tailored to your business goals on Flipkart.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Growth Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ — accordion */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Grow on Flipkart?</h2>
            <p className="sp-cta-desc">Scale your brand with complete Flipkart Marketplace Management Services focused on product visibility, higher conversions, advertising performance, operational efficiency, and long-term marketplace growth.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
